/**
 * xiaDanUser 全局状态（zustand）
 *
 * 原 uniapp 用 vuex 存：
 *   - vuex_user_info   { token, nickname, avatar, mobile }
 *   - vuex_tabbar_list
 *   - vuex_theme_info  CSS 变量字典
 *   - vuex_zone_info   当前选择区域
 *   - vuex_location_info { title, address, location:{lat,lng} }
 *
 * 这里只实现阶段 2 需要的最小集合，后续按需扩展。
 */
"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";

export type UserInfo = {
  token?: string;
  nickname?: string;
  avatar?: string;
  mobile?: string;
};

export type Zone = {
  id?: number;
  name?: string;
  city?: string;
  /** 区域中心坐标（地图默认中心） */
  center_lat?: number;
  center_lng?: number;
};

export type LocationInfo = {
  title?: string;
  address?: string;
  /** 逆地理返回的省/市/区（用于 submit 页面带回起点地址的三段式） */
  province?: string;
  city?: string;
  district?: string;
  location?: { lat: number; lng: number };
};

type State = {
  // 当前 Tab 索引（0=跑腿, 1=我的）
  currentTab: number;
  setCurrentTab: (index: number) => void;

  // 用户信息（持久化到 localStorage）
  userInfo: UserInfo;
  setUserInfo: (u: UserInfo) => void;
  clearUserInfo: () => void;

  // 当前选择区域
  zone: Zone;
  setZone: (z: Zone) => void;

  // 当前定位（起点）
  locationInfo: LocationInfo | null;
  setLocationInfo: (l: LocationInfo | null) => void;
  // 用户主动选择起点（地址簿/地图选点）专用 setter，
  // 强制覆盖 locationManuallySet 拦截，用于用户在地址簿重新选地址
  selectStartLocation: (l: LocationInfo) => void;

  // 标记用户是否手动选过起点地址（地址簿/地图选点），避免自动定位覆盖
  locationManuallySet: boolean;
  setLocationManuallySet: (v: boolean) => void;

  // 终点地址（首页选完地址簿后传入下单页）
  endLocationInfo: LocationInfo | null;
  setEndLocationInfo: (l: LocationInfo | null) => void;

  // 标记用户是否手动选过终点地址（地址簿/地图选点），避免自动定位覆盖
  endManuallySet: boolean;
  setEndManuallySet: (v: boolean) => void;
  // 用户在地址簿主动选过终点标记（强制覆盖）
  selectEndLocation: (l: LocationInfo) => void;
};

export const useAppStore = create<State>()(
  persist(
    (set) => ({
      currentTab: 0,
      setCurrentTab: (index) => set({ currentTab: index }),

      userInfo: {},
      setUserInfo: (u) => set({ userInfo: u }),
      clearUserInfo: () => {
        localStorage.removeItem("xiadanuser_jwt");
        localStorage.removeItem("xiadanuser_refresh");
        set({ userInfo: {} });
      },

      zone: { name: "请选择区域" },
      setZone: (z) => set({ zone: z }),

      locationInfo: null,
      setLocationInfo: (l) => set({ locationInfo: l }),
      // 用户在地址簿主动选过起点标记
      locationManuallySet: false,
      setLocationManuallySet: (v) => set({ locationManuallySet: v }),
      // 跳过拦截强制写入（用户在地址簿重新选起点时用）
      selectStartLocation: (l) =>
        set({ locationInfo: l, locationManuallySet: true }),

      // 终点：默认 null（表示用户没选过终点）
      endLocationInfo: null,
      setEndLocationInfo: (l) => set({ endLocationInfo: l }),
      endManuallySet: false,
      setEndManuallySet: (v) => set({ endManuallySet: v }),
      selectEndLocation: (l) =>
        set({ endLocationInfo: l, endManuallySet: true }),
    }),
    {
      name: "xiadanuser-store",
      // zone + locationInfo 持久化：刷新后保留用户选的区域和地址；
      // locationInfo.title 为默认占位时表示用户还没选过地址（由首页自动定位填充）
      partialize: (state) => ({
        userInfo: state.userInfo,
        zone: state.zone,
        locationInfo: state.locationInfo,
        locationManuallySet: state.locationManuallySet,
        endLocationInfo: state.endLocationInfo,
        endManuallySet: state.endManuallySet,
      }),
    },
  ),
);
